import { LitElement, html, TemplateResult } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { AdoptedStyleSheets } from '../../controllers/adopted-stylesheets';
import innerCSS from './se-button.inner.pcss?inline';
import type { LinkTargetType } from '../se-link/config';
type AriaExpanded = 'true' | 'false';

export type SeButtonVariant = typeof seButtonVariants[number];

export const seButtonVariants = [
  'none',
  'primary',
  'primary-2',
  'registration',
  'secondary',
  'tertiary',
  'dropdown',
  'dropdown-nav',
  'dropdown-alt',
] as const;

export const seButtonSizes = ['slim', 'small', 'medium', 'large'] as const;
export type SeButtonSize = typeof seButtonSizes[number];

/**
 * The Saul-Ewing Button component
 * @element se-button
 * @slot - default slot, used for button text.
 * @slot left - slot used for left side icon.
 * @slot right - slot used for right side icon.
 */
@customElement('se-button')
export class SeButton extends LitElement {
  private stylesheetController = new AdoptedStyleSheets(this, {
    encapsulatedCSS: innerCSS,
  });

  /**
   * The url to use for a link. This will render an anchor element.
   * Do not set this prop if you want to render a button element.
   */
  @property({ type: String, attribute: 'button-url' })
  buttonUrl: string;

  /**
   * ARIA label attribute to pass down to the resulting button or anchor
   * element. Required for accessibility if using a button with icon only.
   */
  @property({ type: String, attribute: 'button-label' })
  buttonLabel: string;

  /**
   * The target to use for a link, used in conjunction with the url attribute.
   */
  @property({ type: String, attribute: 'button-target' })
  buttonTarget: LinkTargetType;

  /**
   * Whether the button is disabled. Only applies to implementations not
   * using the url property.
   */
  @property({ type: Boolean, attribute: 'is-disabled' })
  isDisabled = false;

  /**
   * Value passed to optional aria-controls.
   */
  @property({ type: String })
  controls: string;

  /**
   * Value passed to optional aria-expanded.
   */
  @property({ type: String })
  expanded: AriaExpanded;

  /**
   * A click handler to be passed only to onClick.
   * DO NOT USE @click on this component.
   */
  @property() onClick: () => void;

  /**
   * A keyUp handler to be passed to the onKeyUp.
   * DO NOT USE @keyup on this component.
   */
  @property() onKeyUp: () => void;

  /**
   * The button style variant to use.
   */
  @property({ type: String, attribute: 'variant' })
  variant: SeButtonVariant = 'primary';

  /**
   * The button size to use.
   */
  @property({ type: String, attribute: 'size' })
  size: SeButtonSize = 'medium';

  /**
   * Allow download links.
   */
  @property({ type: Boolean, reflect: true }) downloadable = false;

  updated(): void {
    // Checks is-disabled prop and manages aria-disabled on the host element.
    if (this.hasAttribute('is-disabled')) {
      this.setAttribute('aria-disabled', 'true');
    } else {
      this.setAttribute('aria-disabled', 'false');
    }
  }

  /**
   * Component render function
   * @returns TemplateResult
   */
  render(): TemplateResult {
    return this.buttonUrl
      ? html` <a
          class="btn ${this.variant} ${this.size}"
          href=${this.buttonUrl}
          target=${ifDefined(this.buttonTarget)}
          aria-label="${ifDefined(this.buttonLabel)}"
          aria-disabled="${ifDefined(this.isDisabled)}"
          ?download=${this.downloadable}
        >
          <slot class="slot-icon" name="left"></slot>
          <slot></slot>
          <slot class="slot-icon" name="right"></slot>
        </a>`
      : html`<button
          class="btn ${this.variant} ${this.size}"
          aria-label="${ifDefined(this.buttonLabel)}"
          aria-disabled="${ifDefined(this.isDisabled)}"
          aria-expanded="${ifDefined(
            this.expanded ? this.expanded : undefined
          )}"
          aria-controls="${ifDefined(this.controls)}"
          .onclick="${this.onClick}"
          .onkeyup="${this.onKeyUp}"
          tabindex="-1"
        >
          <slot class="slot-icon" name="left"></slot>
          <slot></slot>
          <slot class="slot-icon" name="right"></slot>
        </button> `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'se-button': SeButton;
  }
}
